import {
  ChatEntityLink,
  ChatProcessLink,
  getSystemLogoUrl,
  getUserAvatarUrl,
  type ChatDocumentDownloader,
  type ChatRendererDataAdapter
} from '@bliks-io/ui';
import { isAppFileDocument, type DownloadOmniscientExportResponse } from '@bliks-io/contracts';
import { apiClient } from '@/api/client';
import { useOrgFunctionsForLookup } from '@/api/queries/useOrgFunctions';
import { useOrgMembersForLookup } from '@/api/queries/useOrgMembers';
import { useSystemsForLookup } from '@/api/queries/useSystems';
import { useAssigneeLookupWithReadiness } from '@/lib/useAssigneeLookup';
import { useProcessForView } from '@/lib/useProcessForView';
import { useProcessLayoutForView } from '@/lib/useProcessLayoutForView';
import { useStepInProcess } from '@/lib/useStepInProcess';
import { redirectBrowser } from '@/lib/browser-redirect';
import { logger } from '@/lib/logger';
import type { ApiError } from '@/lib/agent.types';

/**
 * What the shared chat renderers read from THIS app: its API client, its query
 * hooks and its router. `@bliks-io/ui` owns none of the three, so it declares
 * the seam and this module is the frontend's one implementation of it.
 *
 * Entity views deliberately keep fetching under the VIEWER's own permissions
 * here — the same core browser endpoints they have always used. An app-authority
 * view-data path is post-move work (PRD W4, accepted degradation).
 */

/**
 * Two hops on purpose: the app endpoint authorizes the caller and answers with
 * a short-lived presigned URL, which the browser then fetches WITHOUT an
 * `Authorization` header — the object store rejects a request carrying both a
 * SigV4 signature and that header.
 */
export const downloadDocument: ChatDocumentDownloader = async (document) => {
  // Only Omniscient generated documents, and it did so through the export
  // endpoint; an app file reaches a transcript only inside its own app, which
  // brings its own downloader.
  if (isAppFileDocument(document)) return 'failed';

  try {
    const { data } = await apiClient.get<DownloadOmniscientExportResponse>(document.downloadUrl);
    redirectBrowser(data.downloadUrl);
    return 'done' as const;
  } catch (error) {
    logger.error('Omniscient export download failed', error);
    // The endpoint answers 404 for a swept file and for a foreign one alike —
    // from this user's side both mean "not available any more".
    return (error as ApiError).statusCode === 404 ? ('expired' as const) : ('failed' as const);
  }
};

/**
 * Module-level, and it has to be: its hook members run inside memoised view
 * components on every streamed frame, so a fresh object per render would defeat
 * the memo and churn React's hook identities.
 */
export const chatRendererData: ChatRendererDataAdapter = {
  useUserAvatarUrl(userId) {
    const { data: users } = useOrgMembersForLookup();
    return getUserAvatarUrl(
      users?.find((user) => user._id === userId),
      'thumbnail'
    );
  },
  useSystemLogoUrl(systemId) {
    const { data: systems } = useSystemsForLookup();
    const system = systems?.find((candidate) => candidate._id === systemId);
    return system ? getSystemLogoUrl(system) : undefined;
  },
  useOrgFunctionKind(orgFunctionId) {
    const { data: orgFunctions } = useOrgFunctionsForLookup();
    return orgFunctions?.find((orgFunction) => orgFunction._id === orgFunctionId)?.kind ?? 'team';
  },
  EntityLink: ChatEntityLink,
  ProcessLink: ChatProcessLink,
  useAssigneeLookupWithReadiness,
  useProcessForView,
  useStepInProcess,
  useProcessLayoutForView
};
